import { useClient } from '@cortex/react-query';
import { type FormantBot } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';

import { resolveNamespace, showFormantBots, type ISuiteUrlEnv } from '../../lib/suite-urls';

import { BotAvatar, resolveBotAvatar } from './BotAvatar';
import { BotSettingsDialog } from './BotSettingsDialog';

/**
 * Left-rail "Bots" section. Lists named `agt_formant_bot` threads. These are
 * not user chats — the chat launcher stays on `agt_formant_chat`. Shown only
 * on local and dev.
 */
export function BotsNavSection({ collapsed }: { collapsed: boolean }) {
  const visible = showFormantBots(resolveNamespace(import.meta.env as ISuiteUrlEnv));
  const client = useClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [creating, setCreating] = useState(false);
  const [settingsBot, setSettingsBot] = useState<FormantBot | null>(null);

  const botsQuery = useQuery({
    queryKey: ['formant-bots'],
    enabled: visible,
    queryFn: () => client.http.get('v1/formant-bots').json<FormantBot[]>(),
  });

  if (!visible) return null;

  const bots = botsQuery.data ?? [];
  const collapsedEmpty =
    collapsed && !botsQuery.isLoading && !botsQuery.isError && bots.length === 0;

  return (
    <div className="fk-navrail__bots">
      {collapsedEmpty ? (
        <span
          className="fk-navrail__item fk-tip fk-tip--right fk-navrail__bots-mark"
          data-tip="Bots"
          aria-label="Bots"
        >
          <BotHeadsIcon />
        </span>
      ) : null}
      {collapsedEmpty ? null : (
        <>
          <div className="fk-navrail__label-row">
            <span className="fk-navrail__label">Bots</span>
            {collapsed ? null : (
              <button
                type="button"
                className="fk-navrail__label-add"
                aria-label="New bot"
                title="New bot"
                onClick={() => {
                  setSettingsBot(null);
                  setCreating(true);
                }}
              >
                +
              </button>
            )}
          </div>
          {botsQuery.isLoading ? (
            <div className="fk-navrail__bots-skel" aria-hidden="true">
              <span className="fk-skeleton fk-skeleton--pulse fk-navrail__bots-skel-face" />
              <span className="fk-skeleton fk-skeleton--pulse fk-navrail__bots-skel-name" />
            </div>
          ) : botsQuery.isError ? (
            <div className="fk-navrail__bots-state">Could not load bots.</div>
          ) : (
            <ul className="fk-navrail__bots-list">
              {bots.map((bot) => {
                const to = `/bot/${bot.persistentAgentId}`;
                const active = pathname === to || pathname.startsWith(`${to}/`);
                return (
                  <li key={bot.id} className="fk-navrail__bot">
                    <Link
                      to="/bot/$threadId"
                      params={{ threadId: bot.persistentAgentId }}
                      className={
                        collapsed
                          ? `fk-navrail__item fk-tip fk-tip--right${active ? ' is-active' : ''}`
                          : active
                            ? 'fk-navrail__item is-active'
                            : 'fk-navrail__item'
                      }
                      data-tip={collapsed ? bot.name : undefined}
                      aria-label={bot.name}
                      aria-current={active ? 'page' : undefined}
                    >
                      <span className="fk-navrail__bot-face">
                        <BotAvatar
                          avatar={resolveBotAvatar(bot.avatar, bot.id)}
                          size={22}
                          title={bot.name}
                        />
                      </span>
                      <span className="item-label">{bot.name}</span>
                    </Link>
                    {collapsed ? null : (
                      <button
                        type="button"
                        className="fk-navrail__bot-settings"
                        aria-label={`Settings for ${bot.name}`}
                        title="Bot settings"
                        onClick={() => setSettingsBot(bot)}
                      >
                        <GearIcon />
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
      <BotSettingsDialog
        bot={settingsBot}
        creating={creating}
        onClose={() => {
          setCreating(false);
          setSettingsBot(null);
        }}
      />
    </div>
  );
}

export function BotHeadsIcon({ width = 22, height = 16 }: { width?: number; height?: number }) {
  return (
    <svg
      className="icon"
      viewBox="0 0 24 16"
      style={{ width, height }}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="5" cy="5" r="2.1" />
      <path d="M1.6 14.4c.4-2.3 1.7-3.5 3.4-3.5s3 1.2 3.4 3.5" />
      <circle cx="19" cy="5" r="2.1" />
      <path d="M15.6 14.4c.4-2.3 1.7-3.5 3.4-3.5s3 1.2 3.4 3.5" />
      <circle cx="12" cy="4.2" r="2.4" />
      <path d="M7.7 14.8c.5-2.8 2.1-4.3 4.3-4.3s3.8 1.5 4.3 4.3" />
    </svg>
  );
}

function GearIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1Z" />
    </svg>
  );
}
